"use client";
import * as React from "react";
import {
  Page,
  PageBody,
} from "@constructr-sdk/ui/components/page";
import { useDomainHooks } from "~/context/domain-hooks-context/domain-hooks-context";
import { Label } from "@constructr-sdk/ui/components/label";
import { Textarea } from "@constructr-sdk/ui/components/textarea";
import { Input } from "@constructr-sdk/ui/components/input";
import { Button } from "@constructr-sdk/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@constructr-sdk/ui/components/select";
import { Slider } from "@constructr-sdk/ui/components/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@constructr-sdk/ui/components/dialog";
import {
  AlertCircle,
  AlertTriangle,
  AppWindow,
  CheckCircle2,
  CodeXml,
  Copy,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Info,
  Link as LinkIcon,
  Loader2,
  Palette,
  RefreshCcw,
  ScanSearch,
  SlidersHorizontal,
  Sparkles,
  Type as TypeIcon,
  Wand,
  WandSparkles,
} from "lucide-react";
import { toast } from "sonner";

import { UseLogoGenerator } from "~/modules/main/hooks/use-logo-generator/use-logo-generator.interface";
import { ColorPalette, FontOption, PaletteId } from "@constructr-sdk/entities";

/**
 * View: logo-generator
 * - Single-page split layout:
 *   • Left: Control Panel (prompt, style image upload/URL, palette, size, font, generate).
 *   • Right: Live Preview (SVG container) with persistent Download SVG button.
 * - Business rule: Only valid sanitized SVG is rendered in the preview.
 * - Micro-interactions: Generate button cyan sweep on hover; Download icon spins on successful save.
 */
export function LogoGeneratorView(): React.JSX.Element {
  const { useLogoGenerator } = useDomainHooks();
  // Memoize defaults to avoid re-renders triggering hook side-effects
  const defaults = React.useMemo(
    () => ({
      paletteId: PaletteId.NEON,
      size: 512,
    }),
    [],
  );
  const lg: ReturnType<UseLogoGenerator> = useLogoGenerator({ defaults });

  // Local UI-only states for dialogs
  const [isResetOpen, setIsResetOpen] = React.useState(false);
  const [isViewSourceOpen, setIsViewSourceOpen] = React.useState(false);
  const [justDownloaded, setJustDownloaded] = React.useState(false);
  // Added local UI-only state for custom color picker
  const [customColor, setCustomColor] = React.useState<string>("#0ffbdf");

  const handleValidateUrl = React.useCallback(async (): Promise<void> => {
    try {
      await lg.validateStyleImageUrl();
      if (lg.urlValidationStatus === "valid") {
        toast.success("Style image URL looks valid.");
      } else if (lg.urlValidationStatus === "invalid") {
        toast.error("Image not reachable. Please check the URL.");
      }
    } catch (_e) {
      toast.error("Unable to validate image URL right now. Please try again.");
    }
  }, [lg]);

  const handleGenerate = React.useCallback(async (): Promise<void> => {
    await lg.generateLogo();
    if (lg.error) {
      toast.error(lg.error);
    }
  }, [lg]);

  const handleDownload = React.useCallback(async (): Promise<void> => {
    const filename = await lg.downloadSvg();
    if (filename) {
      setJustDownloaded(true);
      toast.success(`SVG saved as ${filename}`, {
        icon: <CheckCircle2 className="h-4 w-4 text-green-500" />,
      });
      // reset animation state after a tick so it can re-run next time
      setTimeout(() => setJustDownloaded(false), 800);
    }
  }, [lg]);

  const handleCopySvg = React.useCallback(async (): Promise<void> => {
    await lg.copySvgToClipboard();
    toast.success("SVG copied to clipboard");
  }, [lg]);

  const handleOpenInNewTab = React.useCallback((): void => {
    lg.openSvgInNewTab();
  }, [lg]);

  const onResetConfirm = React.useCallback((): void => {
    lg.resetAll();
    setIsResetOpen(false);
  }, [lg]);

  return (
    <Page>
      <PageBody>
        <main
          className="min-h-dvh bg-background text-foreground"
          data-testid="logo-generator-main"
          key="logo-generator-main"
        >
          <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 items-stretch">
              {/* Left: Control Panel */}
              <aside className="md:col-span-5 h-full" key="control-panel">
                <div className="border rounded-lg bg-background h-full flex flex-col" data-testid="control-panel-card">
                  <div className="p-4 sm:p-6 space-y-6 flex-1">
                    {/* Title block */}
                    <header className="flex items-center gap-3" key="title-block">
                      <WandSparkles className="w-5 h-5 text-primary" />
                      <div>
                        <h1 className="text-lg font-semibold tracking-tight">Generate a Logo</h1>
                        <p className="text-xs text-muted-foreground">
                          Describe your logo and optionally provide a style reference.
                        </p>
                      </div>
                    </header>

                    {/* Prompt */}
                    <div className="grid gap-2" key="prompt-input">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="prompt" className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-primary" />
                          Prompt
                        </Label>
                        <span className="text-xs text-muted-foreground">
                          Use concise intent (e.g., “flat, geometric owl mark”)
                        </span>
                      </div>
                      <Textarea
                        id="prompt"
                        placeholder="Describe the logo you want…"
                        aria-label="Text prompt for logo generation"
                        value={lg.prompt}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => lg.setPrompt(e.target.value)}
                        className="h-28 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        data-testid="prompt-textarea"
                      />
                      <p className="text-xs text-muted-foreground">
                        Tips: name the object, vibe, and constraints (flat, outline, geometric).
                      </p>
                    </div>

                    {/* Style image upload and URL */}
                    <div className="grid grid-cols-1 gap-4" key="style-image">
                      {/* Upload */}
                      <div className="grid gap-2" key="style-file">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="style-file" className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4 text-primary" />
                            Style image (optional)
                          </Label>
                          <span className="text-xs text-muted-foreground">PNG/JPG/GIF/WEBP</span>
                        </div>
                        <Input
                          id="style-file"
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0] ?? null;
                            if (file && !['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
                              toast.error('Unsupported image type. Supported: PNG, JPG, GIF, WEBP');
                              e.target.value = '';
                              return;
                            }
                            lg.setStyleImageFile(file);
                          }}
                          aria-label="Upload style image"
                          data-testid="style-file-input"
                        />
                      </div>

                      {/* URL with validation */}
                      <div className="grid gap-2" key="style-url">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="style-url" className="flex items-center gap-2">
                            <LinkIcon className="w-4 h-4 text-primary" />
                            Or link a style image URL
                          </Label>
                          <span className="text-xs text-muted-foreground">We will validate reachability</span>
                        </div>
                        <div className="flex w-full items-center gap-2">
                          <Input
                            id="style-url"
                            type="url"
                            placeholder="https://example.com/reference.png"
                            aria-label="Style image URL"
                            value={lg.styleImageUrl ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              lg.setStyleImageUrl(e.target.value || null)
                            }
                            data-testid="style-url-input"
                          />
                          <Button
                            variant="ghost"
                            onClick={handleValidateUrl}
                            disabled={lg.urlValidationStatus === "validating"}
                            className="gap-2"
                            data-testid="validate-url-btn"
                            aria-label="Validate image URL"
                          >
                            {lg.urlValidationStatus === "validating" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ScanSearch className="h-4 w-4" />
                            )}
                            <span className="hidden sm:inline">Validate</span>
                          </Button>
                        </div>
                        {/* Inline validation helper (toggled by urlValidationStatus) */}
                        {lg.urlValidationStatus === "invalid" ? (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-testid="url-invalid-msg">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Image not reachable.
                          </p>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>

                    {/* Options: Palette, Size, Font */}
                    <div className="space-y-6" key="options-block">
                      {/* Palette */}
                      <div className="grid gap-2" key="palette">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <Palette className="w-4 h-4 text-primary" />
                            Color palette
                          </Label>
                          {/* Replaced the span with a flex container including the color picker */}
                          <div className="flex items-center gap-2">
                            <Input
                              id="custom-color"
                              type="color"
                              value={customColor}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomColor(e.target.value)}
                              aria-label="Custom color picker"
                              data-testid="custom-color-picker"
                              className="h-8 w-10 p-1 bg-transparent border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                            />
                            <span className="text-xs text-muted-foreground hidden sm:inline">Pick one</span>
                          </div>
                        </div>
                        <PaletteGrid
                          palettes={lg.paletteOptions}
                          selectedId={lg.selectedPaletteId}
                          onSelect={lg.setSelectedPaletteId}
                        />
                        <p className="text-xs text-muted-foreground">
                          Primary actions will use the selected palette when generating.
                        </p>
                      </div>

                      {/* Size */}
                      <div className="grid gap-2" key="size">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <SlidersHorizontal className="w-4 h-4 text-primary" />
                            Size
                          </Label>
                          <span className="text-xs text-muted-foreground">
                            <span className="font-medium">{lg.size}px</span>
                          </span>
                        </div>
                        <Slider
                          value={[lg.size]}
                          min={128}
                          max={1024}
                          step={1}
                          onValueChange={(vals: number[]) => {
                            const v = (vals?.[0] ?? 512) as number;
                            lg.setSize(v);
                          }}
                          onValueCommit={(vals: number[]) => {
                            const v = (vals[0] ?? 512) as number;
                            lg.setSize(v);
                          }}
                          aria-label="Logo size slider"
                          aria-valuetext={`${lg.size}px`}
                          data-testid="size-slider"
                        />
                        <div className="flex justify-between px-1 text-[10px] text-muted-foreground mt-1">
                          <span>128</span>
                          <span>256</span>
                          <span>512</span>
                          <span>768</span>
                          <span>1024</span>
                        </div>
                      </div>

                      {/* Font */}
                      <div className="grid gap-2" key="font">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-primary" />
                            Font
                          </Label>
                          <span className="text-xs text-muted-foreground">
                            Used for text elements inside the logo
                          </span>
                        </div>
                        <Select
                          value={lg.selectedFontId}
                          onValueChange={(val: string) => lg.setSelectedFontId(val)}
                        >
                          <SelectTrigger className="w-full" data-testid="font-select-trigger">
                            <SelectValue placeholder="Select a font" />
                          </SelectTrigger>
                          <SelectContent>
                            {lg.fontOptions.map((f: FontOption) => (
                              <SelectItem value={f.id} key={`font-${f.id}`}>
                                {f.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-2" key="actions">
                      {/* NOTE FROM DESIGN: Generate button with animated cyan sweep on hover */}
                      <Button
                        className="relative overflow-hidden flex-1 gap-2"
                        onClick={handleGenerate}
                        disabled={lg.isGenerating || !lg.prompt.trim()}
                        data-testid="generate-btn"
                      >
                        {lg.isGenerating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wand className="w-4 h-4" />
                        )}
                        <span className="relative z-10">Generate Logo</span>
                        {/* sweep layer */}
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-accent/40 to-transparent group-hover:translate-x-0 motion-reduce:transition-none transition-transform duration-300"
                        />
                      </Button>

                      <Button
                        variant="ghost"
                        className="gap-2"
                        onClick={() => setIsResetOpen(true)}
                        data-testid="reset-btn"
                      >
                        <RefreshCcw className="w-4 h-4" />
                        Reset
                      </Button>
                    </div>

                    {/* Status helpers */}
                    <div className="space-y-3" key="status-helpers">
                      {/* Loading */}
                      {lg.isGenerating ? (
                        <div className="border rounded-md p-3 flex items-start gap-3" data-testid="loading-banner">
                          <Loader2 className="w-4 h-4 animate-spin mt-0.5" />
                          <div>
                            <h4 className="font-medium">Generating…</h4>
                            <p className="text-xs text-muted-foreground">
                              We’re creating your SVG. This usually takes a few seconds.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}

                      {/* Error */}
                      {lg.error ? (
                        <div className="border border-red-300 rounded-md p-3 flex items-start gap-3" data-testid="error-banner">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-red-700">Generation failed</h4>
                            <p className="text-xs text-red-600">{lg.error}</p>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
              </aside>

              {/* Right: Preview Panel */}
              <section className="md:col-span-7 h-full" key="preview-panel">
                <div className="border rounded-lg overflow-hidden h-full flex flex-col" data-testid="preview-card">
                  {/* Sticky Actions Row */}
                  <div className="sticky top-0 z-10 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <AppWindow className="w-4 h-4 text-primary" />
                        <span>Live Preview</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2"
                          onClick={() => setIsViewSourceOpen(true)}
                          aria-label="View SVG source"
                          data-testid="view-source-btn"
                        >
                          <CodeXml className="w-4 h-4" />
                          <span className="hidden sm:inline">View Source</span>
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="gap-2"
                          onClick={handleDownload}
                          data-testid="download-btn"
                        >
                          <Download
                            className={`w-4 h-4 ${justDownloaded ? "animate-spin-once" : ""}`}
                          />
                          <span>Download SVG</span>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Preview area */}
                  <div className="p-4 sm:p-6 flex-1">
                    {/* Conditional rendering: only one section visible */}
                    {lg.isGenerating ? (
                      <div className="border border-dashed rounded-md p-4" data-testid="generation-progress" role="progressbar" aria-label="Generating logo">
                        <div className="text-sm font-medium mb-2">Generating your SVG…</div>
                        <div className="h-2 w-full overflow-hidden rounded bg-muted">
                          <div className="h-2 w-1/3 bg-primary/70 animate-[neon-sweep_1.2s_ease-in-out_infinite]" aria-hidden />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Optimizing vectors and applying palette/font. This usually takes 5–10 seconds.</p>
                      </div>
                    ) : lg.svgMarkup ? (
                      <>
                        <div className="border rounded-md w-full grid place-items-center bg-muted/40 overflow-auto" data-testid="svg-preview-container">
                          <div style={{ width: `${lg.size}px`, height: `${lg.size}px` }} className="max-w-full max-h-full">
                            <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: lg.svgMarkup }} />
                          </div>
                        </div>
                        {/* Caption row */}
                        <div className="mt-3 flex items-center justify-between" data-testid="preview-caption">
                          <div className="text-xs text-muted-foreground">
                            Size: <span className="font-medium">{lg.size}px</span> • Palette:{" "}
                            <span className="font-medium">
                              {lg.paletteOptions.find((p: ColorPalette) => p.id === lg.selectedPaletteId)?.name ??
                                lg.selectedPaletteId}
                            </span>{" "}
                            • Font:{" "}
                            <span className="font-medium">
                              {lg.fontOptions.find((f: FontOption) => f.id === lg.selectedFontId)?.name ??
                                lg.selectedFontId}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1"
                              onClick={handleCopySvg}
                              aria-label="Copy SVG"
                              data-testid="copy-svg-btn"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              Copy
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1"
                              onClick={handleOpenInNewTab}
                              aria-label="Open in new tab"
                              data-testid="open-svg-btn"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Open
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="border border-dashed rounded-md p-4 flex items-start gap-3 bg-muted/30" data-testid="empty-state">
                        <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div>
                          <h4 className="font-medium">No logo yet</h4>
                          <p className="text-xs text-muted-foreground">
                            Enter a prompt and click Generate to see your logo here.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </section>
        </main>

        {/* Reset confirmation dialog */}
        <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
          <DialogContent className="sm:max-w-md" data-testid="reset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCcw className="w-5 h-5 text-primary" />
                Reset all settings?
              </DialogTitle>
              <DialogDescription>
                This will clear your prompt and revert palette, size, and font selections.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-end">
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button onClick={onResetConfirm} data-testid="confirm-reset-btn">
                Reset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View source dialog */}
        <Dialog open={isViewSourceOpen} onOpenChange={setIsViewSourceOpen}>
          <DialogContent className="sm:max-w-3xl" data-testid="view-source-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CodeXml className="w-5 h-5 text-primary" />
                SVG Source
              </DialogTitle>
              <DialogDescription>Inspect or copy the sanitized SVG markup.</DialogDescription>
            </DialogHeader>
            <div className="bg-muted border rounded-md p-3 max-h-80 overflow-auto text-xs font-mono">
              {lg.svgMarkup ? (
                <code className="whitespace-pre-wrap break-words">{lg.svgMarkup}</code>
              ) : (
                <span className="text-muted-foreground">No SVG generated yet.</span>
              )}
            </div>
            <DialogFooter className="sm:justify-end">
              <DialogClose asChild>
                <Button variant="ghost">Close</Button>
              </DialogClose>
              <Button className="gap-2" onClick={handleCopySvg} disabled={!lg.svgMarkup}>
                <Copy className="w-4 h-4" />
                Copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </Page>
  );
}

/**
 * PaletteGrid
 * - Renders a grid of selectable palettes with visible selection ring.
 * - Uses semantic buttons and keyboard focus rings for accessibility.
 * - NOTE: All colors should come from theme tokens; swatches are for preview only.
 */
function PaletteGrid(props: {
  palettes: ColorPalette[];
  selectedId: PaletteId;
  onSelect: (id: PaletteId) => void;
}): React.JSX.Element {
  const { palettes, selectedId, onSelect } = props;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="palette-grid" key="palette-grid">
      {palettes.map((p: ColorPalette, idx: number) => {
        const isSelected = p.id === selectedId;
        return (
          <button
            type="button"
            key={`palette-${p.id}-${idx}`}
            data-testid={`palette-${p.id}`}
            onClick={(): void => onSelect(p.id)}
            className={[
              "relative aspect-[5/3] w-full border text-left overflow-hidden rounded-md",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              isSelected ? "ring-2 ring-accent border-transparent" : "border-border hover:bg-muted/40",
            ].join(" ")}
            aria-pressed={isSelected}
            aria-label={`Palette ${p.name}`}
          >
            {/* gradient hint using first two swatches if available */}
            <span
              aria-hidden
              className="absolute inset-0 opacity-20"
              style={{
                background:
                  p.swatches.length >= 2
                    ? `linear-gradient(90deg, ${p.swatches[0]} 0%, ${p.swatches[1]} 100%)`
                    : undefined,
              }}
            />
            {/* tiny swatch row */}
            <div className="absolute right-1 bottom-1 flex gap-0.5">
              {p.swatches.slice(0, 4).map((hex: string, i: number) => (
                <span
                  key={`sw-${p.id}-${i}`}
                  className="h-2 w-3 rounded-sm border border-white/40"
                  style={{ backgroundColor: hex }}
                />
              ))}
            </div>
            <span className="sr-only">Palette {p.name}</span>
          </button>
        );
      })}
    </div>
  );
}